import React, { useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Tooltip, IconButton,
} from '@mui/material';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';
import {
  SYNC_PROVIDER_LABELS,
  SYNC_TYPE_LABELS,
  SYNC_MODE_LABELS,
  SYNC_TRIGGER_COLORS,
  SYNC_RUN_STATUS_LABELS,
  SYNC_RUN_STATUS_COLORS,
  RESULT_STATUSES,
} from '../../../constants/ingestionSyncRuns';
import {
  formatDuration, describeBatchPosition, describeMappingSet, groupRunsByBatch, describeTrigger,
} from '../../../utils/ingestionSyncRuns';
import {
  RunRow, TableWrapper, TwoLine, TwoLinePrimary, TwoLineSecondary,
} from './styles';
import { getChipSx } from '../DirectManagerDiscrepancies/chipStyles';

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const StatusCell = ({ status, incomplete }) => (
  <TableCell>
    <Chip
      size="small"
      label={SYNC_RUN_STATUS_LABELS[status] || status}
      sx={getChipSx(SYNC_RUN_STATUS_COLORS[status])}
    />
    {incomplete && (
      <Tooltip arrow title="Counters are stored, but some result parts never arrived — item lists are partial">
        <Chip size="small" label="Partial result" sx={{ ...getChipSx('warning'), ml: 0.5 }} />
      </Tooltip>
    )}
  </TableCell>
);

const CounterCells = ({ row }) => (
  <>
    <TableCell align="right">{row.createdCount ?? 0}</TableCell>
    <TableCell align="right">{row.updatedCount ?? 0}</TableCell>
    <TableCell align="right">{row.skippedCount ?? 0}</TableCell>
    <TableCell align="right" sx={row.errorCount > 0 ? { color: '#991B1B' } : undefined}>
      {row.errorCount ?? 0}
    </TableCell>
  </>
);

const ModeAndTrigger = ({ row }) => (
  <TwoLineSecondary>
    {`${SYNC_MODE_LABELS[row.mode] || row.mode} · `}
    <span style={SYNC_TRIGGER_COLORS[row.trigger] ? { color: SYNC_TRIGGER_COLORS[row.trigger] } : undefined}>
      {describeTrigger(row)}
    </span>
  </TwoLineSecondary>
);

const RunCells = ({ run, child }) => (
  <>
    <TableCell sx={child ? { pl: 7 } : undefined}>
      <TwoLine>
        <TwoLinePrimary>
          {child
            ? SYNC_TYPE_LABELS[run.syncType] || run.syncType
            : `${SYNC_PROVIDER_LABELS[run.provider] || run.provider} · ${SYNC_TYPE_LABELS[run.syncType] || run.syncType}`}
          {run.scope === 'SCOPED' && (
            <Tooltip arrow title="Filtered to part of the population (e.g. one employee for a bench candidate import)">
              <Chip size="small" label="Scoped" sx={{ ...getChipSx('default'), ml: 1, height: 20 }} />
            </Tooltip>
          )}
        </TwoLinePrimary>
        {!child && <ModeAndTrigger row={run} />}
      </TwoLine>
    </TableCell>
    <TableCell>{describeBatchPosition(run)}</TableCell>
    <TableCell>{formatDateTime(run.startedAt)}</TableCell>
    <TableCell>{formatDuration(run.durationMs)}</TableCell>
    <StatusCell status={run.status} incomplete={run.resultStatus === RESULT_STATUSES.INCOMPLETE} />
    <CounterCells row={run} />
    <TableCell>{describeMappingSet(run)}</TableCell>
  </>
);

const BatchRow = ({ group, expanded, onToggle }) => (
  <RunRow onClick={onToggle} style={{ borderTop: '2px solid #e2e8f0' }}>
    <TableCell>
      <TwoLine style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <IconButton
          size="small"
          aria-label={expanded ? 'Collapse batch' : 'Expand batch'}
          onClick={(event) => { event.stopPropagation(); onToggle(); }}
        >
          {expanded ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
        </IconButton>
        <TwoLine>
          <TwoLinePrimary>{`${SYNC_PROVIDER_LABELS[group.provider] || group.provider} · Pipeline`}</TwoLinePrimary>
          <ModeAndTrigger row={group} />
        </TwoLine>
      </TwoLine>
    </TableCell>
    <TableCell>{`${group.runs.length}/${group.stepCount} steps`}</TableCell>
    <TableCell>{formatDateTime(group.startedAt)}</TableCell>
    <TableCell>{formatDuration(group.durationMs)}</TableCell>
    <StatusCell status={group.status} incomplete={group.hasIncompleteResult} />
    <CounterCells row={group} />
    <TableCell>{describeMappingSet(group.mappingRun)}</TableCell>
  </RunRow>
);

const RunsTable = ({ runs, onRowClick }) => {
  // Batches start collapsed; the set holds the expanded ones.
  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = (key) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    return next;
  });

  return (
    <TableWrapper>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Sync</TableCell>
              <TableCell>Batch</TableCell>
              <TableCell>Started</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Created</TableCell>
              <TableCell align="right">Updated</TableCell>
              <TableCell align="right">Skipped</TableCell>
              <TableCell align="right">Errors</TableCell>
              <TableCell>Mappings</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupRunsByBatch(runs).map((row) => {
              if (row.type === 'run') {
                return (
                  <RunRow key={row.key} onClick={() => onRowClick && onRowClick(row.run)} style={{ borderTop: '2px solid #e2e8f0' }}>
                    <RunCells run={row.run} />
                  </RunRow>
                );
              }
              const isOpen = expanded.has(row.key);
              return (
                <React.Fragment key={row.key}>
                  <BatchRow group={row} expanded={isOpen} onToggle={() => toggle(row.key)} />
                  {isOpen && row.runs.map((run) => (
                    <RunRow key={run.id} onClick={() => onRowClick && onRowClick(run)} style={{ background: '#fafbfc' }}>
                      <RunCells run={run} child />
                    </RunRow>
                  ))}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </TableWrapper>
  );
};

export default RunsTable;
